/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { EmailValidator, NotEmptyValidator } from '@squiz/dxp-console-support';
import { IdpLoginRequest, IdpSettingsMissing, SamlLoginRequest } from '@squiz/dxp-identity-gateway-types';
import {
  BUTTON_TYPE_SUBMIT,
  Button,
  ButtonProps,
  FORM_THEME_LITE,
  Form,
  FormProps,
  ICON_PERSON,
  TextField,
  TextFieldProps,
} from '@squiz/sds';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { DXP_LOCAL_SESSION_KEY } from '../../../../Constants/App.constants';
import { CORE_STRINGS } from '../../../../Constants/Strings.constants';
import { useRedirectPath } from '../../../../Hooks/UseRedirectPath/UseRedirectPath.hook';
import { getIdentityGatewayService } from '../../../../Services/identity-gateway';
import { LOGIN_FORM_DATA_IDS, LOGIN_FORM_IDS } from './LoginForm.constants';

/**
 * The Login form.
 */
export const LoginForm = (): JSX.Element => {
  const { t } = useTranslation();

  // Identity Gateway service
  const identityGatewayService = getIdentityGatewayService();

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);

  const from = useRedirectPath();

  // Validators
  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  const emailValidator = useMemo(() => {
    return new EmailValidator<boolean>((): boolean => false);
  }, []);

  // Set up strings
  const labels = useMemo(() => {
    return {
      email: t(CORE_STRINGS.email),
      errorUnknown: t(CORE_STRINGS.errorUnknown),
      invalidEmail: t(CORE_STRINGS.invalidEmail),
      requiredField: t(CORE_STRINGS.requiredField),
      signInButton: t(CORE_STRINGS.signIn),
      title: t(CORE_STRINGS.signIn),
    };
  }, [t]);

  // Handle navigation
  const navigate = useNavigate();
  const handleNavigation = useCallback(
    (itemPath: string): void => {
      navigate(itemPath);
    },
    [navigate],
  );

  const loginHandler = useCallback(
    (dto: IdpLoginRequest): void => {
      lastValueFrom(identityGatewayService.logInToIdp(dto))
        .then((response: SamlLoginRequest | IdpSettingsMissing) => {
          const idpResponse = response as SamlLoginRequest;
          const noSettingResponse = response as IdpSettingsMissing;

          // Handle IdP redirect
          if (idpResponse.context) {
            window.open(idpResponse.context, '_self');
          }

          // Handle Auth0 redirect
          if (noSettingResponse.message) {
            const tokenData = { sessionId: noSettingResponse.id };
            const sessionCode = btoa(JSON.stringify(tokenData));

            // Store session for Auth0 round-trip
            localStorage.setItem(DXP_LOCAL_SESSION_KEY, sessionCode);

            handleNavigation(`/dxp-login`);
          }

          return true;
        })
        .catch((error: AjaxError) => {
          // eslint-disable-next-line no-console
          console.error(error);

          setFormError(labels.errorUnknown);
          setSubmit(false);
          return true;
        });
    },
    [handleNavigation, identityGatewayService, labels],
  );

  const submitHandler = useCallback((): void => {
    setFormError(``);

    const emailInput = emailRef.current as HTMLInputElement;
    const emailValue = emailInput.value;

    // Validate field input
    const nextErrors = { ...fieldErrors };
    const fieldId = emailInput.getAttribute(`id`) as string;
    delete nextErrors[fieldId];

    if (!notEmptyValidator.validate(String(emailValue))) {
      nextErrors[fieldId] = labels.requiredField;
    } else if (!emailValidator.validate(String(emailValue))) {
      nextErrors[fieldId] = labels.invalidEmail;
    }

    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length !== 0) {
      return;
    }

    // Submit the form
    setSubmit(true);

    loginHandler({
      email: emailValue,
      origin: window.location.origin,
      redirectPath: from,
    });
  }, [emailValidator, fieldErrors, from, labels, loginHandler, notEmptyValidator]);

  // Email field
  const emailRef = useRef<HTMLInputElement>(null);
  const emailProps: TextFieldProps = {
    autoComplete: 'email',
    errorMessage: fieldErrors[LOGIN_FORM_IDS.email] || null,
    fieldLabel: labels.email,
    hideLabel: true,
    icon: ICON_PERSON,
    id: LOGIN_FORM_IDS.email,
    name: LOGIN_FORM_IDS.email,
    placeholder: labels.email,
    value: ``,
  };

  // Sign in button
  const signInButtonKey = `sign-in`;
  const signInButtonRef = useRef<HTMLButtonElement>(null);
  const signInButtonProps: ButtonProps = {
    classes: `user-management-form__sign-in-btn`,
    dataId: LOGIN_FORM_DATA_IDS.signInButton,
    id: LOGIN_FORM_IDS.login,
    isLoading: isFormSubmitted,
    onClick: submitHandler,
    text: labels.signInButton,
    type: BUTTON_TYPE_SUBMIT,
  };
  const signInButton = <Button {...signInButtonProps} ref={signInButtonRef} />;

  // Configure the form
  const formProps = {
    buttonKeys: [signInButtonKey],
    buttons: [signInButton],
    classes: `login-form`,
    errorMessage: formError,
    legend: labels.title,
    theme: FORM_THEME_LITE,
  } as FormProps;

  return (
    <div className="user-management-form__wrapper login-form__wrapper">
      <div className="user-management-form">
        <h2 className="user-management-form__heading">{labels.title}</h2>
        <Form {...formProps}>
          <TextField {...emailProps} data-id={LOGIN_FORM_DATA_IDS.emailField} ref={emailRef} />
        </Form>
      </div>
    </div>
  );
};
